import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { ThemeContextType, ThemeType, CorporatePartnerBranding } from './types';
import { LIGHT_THEME_USER_TYPES, normalizeMemberType } from './classes';
import { getContrastColor, adjustColorBrightness, hexToRgba } from '@member-portal/utils';

// Default theme color values
const DARK_THEME_COLORS = {
  '--theme-bg': '#0a0a0a',
  '--theme-bg-secondary': '#262626',
  '--theme-bg-card': '#171717',
  '--theme-bg-hover': '#262626',
  '--theme-bg-active': 'rgba(255, 255, 255, 0.1)',
  '--theme-text': '#ffffff',
  '--theme-text-secondary': '#d4d4d4',
  '--theme-text-muted': '#a3a3a3',
  '--theme-accent': '#ffffff',
  '--theme-accent-text': '#000000',
  '--theme-border': '#404040',
  '--theme-border-secondary': '#525252',
  '--theme-border-accent': '#ffffff',
};

const LIGHT_THEME_COLORS = {
  '--theme-bg': '#ffffff',
  '--theme-bg-secondary': '#ffffff',
  '--theme-bg-card': '#ffffff',
  '--theme-bg-hover': '#f9fafb',
  '--theme-bg-active': 'rgba(0, 0, 0, 0.1)',
  '--theme-text': '#000000',
  '--theme-text-secondary': '#374151',
  '--theme-text-muted': '#4b5563',
  '--theme-accent': '#000000',
  '--theme-accent-text': '#ffffff',
  '--theme-border': '#e5e7eb',
  '--theme-border-secondary': '#d1d5db',
  '--theme-border-accent': '#000000',
};

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode;
  /** Directly set the theme - takes priority over initialUserType */
  initialTheme?: ThemeType;
  /** Set theme based on member tier (for member apps) */
  initialUserType?: string | null;
  initialCorporatePartnerId?: string | null;
  fetchPartnerBranding?: (partnerId: string) => Promise<CorporatePartnerBranding | null>;
}

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialTheme,
  initialUserType = null,
  initialCorporatePartnerId = null,
  fetchPartnerBranding,
}) => {
  const [userType, setUserType] = useState<string | null>(initialUserType);
  const [currentTheme, setCurrentTheme] = useState<ThemeType>(initialTheme || 'light');
  const [corporatePartnerId, setCorporatePartnerId] = useState<string | null>(initialCorporatePartnerId);
  const [partnerBranding, setPartnerBranding] = useState<CorporatePartnerBranding | null>(null);

  // Update userType when initialUserType changes
  useEffect(() => {
    setUserType(initialUserType);
  }, [initialUserType]);

  // Update corporatePartnerId when initialCorporatePartnerId changes
  useEffect(() => {
    setCorporatePartnerId(initialCorporatePartnerId);
  }, [initialCorporatePartnerId]);

  // Fetch partner branding when corporatePartnerId changes
  useEffect(() => {
    const loadPartnerBranding = async () => {
      if (!corporatePartnerId) {
        setPartnerBranding(null);
        return;
      }

      if (fetchPartnerBranding) {
        try {
          const branding = await fetchPartnerBranding(corporatePartnerId);
          setPartnerBranding(branding);
        } catch (error) {
          console.error('ThemeContext: Error fetching corporate partner branding', error);
          setPartnerBranding(null);
        }
      }
    };

    loadPartnerBranding();
  }, [corporatePartnerId, fetchPartnerBranding]);

  // Check if this is an Executive tier corporate partner
  const isExecutiveTierCorporate = useMemo(() => {
    return partnerBranding?.corporateTier?.toLowerCase() === 'executive';
  }, [partnerBranding?.corporateTier]);

  // Determine effective user type
  const effectiveUserType = useMemo(() => {
    if (partnerBranding?.corporateTier) {
      if (isExecutiveTierCorporate) {
        return 'core';
      }
      return normalizeMemberType(partnerBranding.corporateTier);
    }
    return userType ? normalizeMemberType(userType) : null;
  }, [userType, partnerBranding?.corporateTier, isExecutiveTierCorporate]);

  // Determine if light theme: initialTheme takes priority, then userType-based logic
  const isLightTheme = initialTheme
    ? initialTheme === 'light'
    : effectiveUserType
      ? LIGHT_THEME_USER_TYPES.includes(effectiveUserType)
      : true;

  // Apply theme CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const newTheme = isLightTheme ? 'light' : 'dark';
    setCurrentTheme(newTheme);

    // Update document body classes
    document.body.classList.remove('theme-dark', 'theme-light');
    document.body.classList.add(`theme-${newTheme}`);
    document.documentElement.setAttribute('data-theme', newTheme);

    // Set base theme colors
    const baseColors = isLightTheme ? LIGHT_THEME_COLORS : DARK_THEME_COLORS;
    Object.entries(baseColors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Override with partner colors if available
    if (partnerBranding && !isExecutiveTierCorporate) {
      const bgContrast = getContrastColor(partnerBranding.backgroundColor);
      const accentContrast = getContrastColor(partnerBranding.accentColor);

      root.style.setProperty('--theme-bg', partnerBranding.backgroundColor);
      root.style.setProperty('--theme-bg-secondary', adjustColorBrightness(partnerBranding.surfaceColor, 5));
      root.style.setProperty('--theme-bg-card', partnerBranding.surfaceColor);
      root.style.setProperty('--theme-bg-hover', adjustColorBrightness(partnerBranding.surfaceColor, 10));
      root.style.setProperty('--theme-bg-active', hexToRgba(partnerBranding.accentColor, 0.15));
      root.style.setProperty('--theme-text', bgContrast);
      root.style.setProperty('--theme-text-secondary', hexToRgba(bgContrast, 0.85));
      root.style.setProperty('--theme-text-muted', hexToRgba(bgContrast, 0.6));
      root.style.setProperty('--theme-accent', partnerBranding.accentColor);
      root.style.setProperty('--theme-accent-text', accentContrast);
      root.style.setProperty('--theme-border', hexToRgba(partnerBranding.accentColor, 0.5));
      root.style.setProperty('--theme-border-secondary', hexToRgba(partnerBranding.accentColor, 0.35));
      root.style.setProperty('--theme-border-accent', partnerBranding.accentColor);

      root.setAttribute('data-partner', corporatePartnerId || '');
    } else {
      root.removeAttribute('data-partner');
    }
  }, [isLightTheme, partnerBranding, corporatePartnerId, isExecutiveTierCorporate]);

  const handleSetUserType = (newUserType: string | null) => {
    setUserType(newUserType);
  };

  const handleSetTheme = (theme: ThemeType) => {
    setCurrentTheme(theme);
    document.body.classList.remove('theme-dark', 'theme-light');
    document.body.classList.add(`theme-${theme}`);
    document.documentElement.setAttribute('data-theme', theme);
  };

  const value: ThemeContextType = {
    currentTheme,
    userType,
    setUserType: handleSetUserType,
    setTheme: handleSetTheme,
    isLightTheme,
    corporatePartnerId,
    setCorporatePartnerId,
    partnerBranding,
    setPartnerBranding,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
